import Cookies from 'js-cookie'
import { handleSessionExpired } from '@/lib/authToken'

const API_BASE = (import.meta.env?.VITE_API_URL as string) || ''

function buildUrl(path: string) {
  if (API_BASE) return `${API_BASE.replace(/\/+$/, '')}/${path.replace(/^\/+/, '')}`
  return path.startsWith('/') ? path : `/${path}`
}

type ApiError = Error & { statusCode: number }

async function apiFetch<T>(method: string, path: string): Promise<T> {
  const token = Cookies.get('token')
  const headers: Record<string, string> = {}
  if (token) headers.Authorization = `Bearer ${token}`
  const res = await fetch(buildUrl(path), { method, headers })
  if (res.status === 204) return undefined as T
  if (!res.ok) {
    if (res.status === 401 && token) {
      handleSessionExpired()
      const err = new Error('Session expired') as ApiError
      err.statusCode = 401
      throw err
    }
    const payload = await res.json().catch(() => null)
    const details = Array.isArray(payload?.error?.details) ? payload.error.details : null
    const detailMessage = details?.length
      ? details.map((d: { field?: string; message?: string }) => d.message || d.field).join('; ')
      : null
    const err = new Error(detailMessage || payload?.error?.message || payload?.message || res.statusText || 'Request failed') as ApiError
    err.statusCode = res.status
    throw err
  }
  return res.json()
}

export async function getHubSpotOAuthUrl(workspaceId: string): Promise<string> {
  const data = await apiFetch<{ url: string }>('GET', `/api/workspaces/${workspaceId}/hubspot/oauth/start`)
  return data.url
}

export async function disconnectHubSpot(workspaceId: string): Promise<void> {
  await apiFetch<void>('DELETE', `/api/workspaces/${workspaceId}/hubspot`)
}

export async function verifyHubSpot(workspaceId: string): Promise<unknown> {
  return apiFetch<unknown>('POST', `/api/workspaces/${workspaceId}/hubspot/verify`)
}

export async function setupHubSpotProperties(workspaceId: string): Promise<void> {
  await apiFetch<void>('POST', `/api/workspaces/${workspaceId}/hubspot/setup`)
}

export type QuickContact = { id: string; name: string; email: string; company: string }
export type QuickDeal = { id: string; name: string; amount: string; stage: string; closeDate: string }
export type QuickCompany = { id: string; name: string; domain: string; industry: string }

export async function searchContactsQuick(workspaceId: string, query: string): Promise<QuickContact[]> {
  const data = await apiFetch<{ contacts: QuickContact[] }>('GET', `/api/workspaces/${workspaceId}/hubspot/contacts/search?q=${encodeURIComponent(query)}`)
  return data.contacts || []
}

export async function searchDealsQuick(workspaceId: string, query: string): Promise<QuickDeal[]> {
  const data = await apiFetch<{ deals: QuickDeal[] }>('GET', `/api/workspaces/${workspaceId}/hubspot/deals/search?q=${encodeURIComponent(query)}`)
  return data.deals || []
}

export async function searchCompaniesQuick(workspaceId: string, query: string): Promise<QuickCompany[]> {
  const data = await apiFetch<{ companies: QuickCompany[] }>('GET', `/api/workspaces/${workspaceId}/hubspot/companies/search?q=${encodeURIComponent(query)}`)
  return data.companies || []
}
